import React, { useState, useEffect } from "react";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL;

function isInvoiceInSelectedFys(inv: any, selectedFys: string[]): boolean {
  if (!selectedFys || selectedFys.length === 0) return true;
  if (inv.financial_year) {
    return selectedFys.includes(inv.financial_year);
  }
  const d = new Date(inv.Date);
  if (isNaN(d.getTime())) return false;
  const year = d.getFullYear();
  const month = d.getMonth(); // 0-indexed: 0 = Jan
  let fy = "";
  if (month >= 3) { // Apr or later
    fy = `${String(year).slice(-2)}-${String(year + 1).slice(-2)}`;
  } else {
    fy = `${String(year - 1).slice(-2)}-${String(year).slice(-2)}`;
  }
  return selectedFys.includes(fy);
}

interface Company {
  id: number | string;
  company_name: string;
}

interface Invoice {
  id: number;
  Date: string;
  Total_Amount: number;
  SGSTAmount: number;
  CGSTAmount: number;
  IGSTAmount: number;
  InvoiceNo?: string;
}

const GstReport: React.FC = () => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [salesInvoices, setSalesInvoices] = useState<Invoice[]>([]);
  const [purchaseInvoices, setPurchaseInvoices] = useState<Invoice[]>([]);
  
  const [companyId, setCompanyId] = useState<string>("");
  const [month, setMonth] = useState<number>(new Date().getMonth());
  const [reportType, setReportType] = useState<'monthly' | 'quarterly'>('monthly');
  const [quarter, setQuarter] = useState<number>(1); // 1 = Q1 (Apr-Jun), 2 = Q2 (Jul-Sep), 3 = Q3 (Oct-Dec), 4 = Q4 (Jan-Mar)
  const [year, setYear] = useState<number>(new Date().getFullYear());
  
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    axios.get(`${API_URL}/auth/companies`).then((res) => {
      setCompanies(res.data);
    });
  }, []);

  useEffect(() => {
    if (!companyId) return;
    setLoading(true);
    Promise.all([
      axios.get(`${API_URL}/auth/invoices`),
      axios.get(`${API_URL}/auth/purchase-invoices`),
      axios.get(`${API_URL}/auth/setting`)
    ]).then(([salesRes, purchRes, settingRes]) => {
      let sales = salesRes.data || [];
      let purchases = purchRes.data || [];
      const settingData = settingRes.data || {};

      let selectedFys: string[] = [];
      try {
        if (settingData.selected_fys) {
          selectedFys = JSON.parse(settingData.selected_fys);
        }
      } catch (e) {}

      if (selectedFys.length > 0) {
        sales = sales.filter((inv: any) => isInvoiceInSelectedFys(inv, selectedFys));
        purchases = purchases.filter((inv: any) => isInvoiceInSelectedFys(inv, selectedFys));
      }

      sales = sales.filter((inv: any) => String(inv.Company_id) === String(companyId));
      purchases = purchases.filter((inv: any) => String(inv.Company_id) === String(companyId));
      setSalesInvoices(sales);
      setPurchaseInvoices(purchases);
      setLoading(false);
    }).catch(() => {
      setLoading(false);
    });
  }, [companyId]);

  const filterByPeriod = (invoices: Invoice[]) => {
    return invoices.filter(inv => {
      const d = new Date(inv.Date);
      if (reportType === 'monthly') {
        return d.getMonth() === month && d.getFullYear() === year;
      } else {
        const m = d.getMonth(); // 0 = Jan, 11 = Dec
        const y = d.getFullYear();
        // Financial Year quarters:
        // Q1: Apr(3) - Jun(5) of selected 'year'
        // Q2: Jul(6) - Sep(8) of selected 'year'
        // Q3: Oct(9) - Dec(11) of selected 'year'
        // Q4: Jan(0) - Mar(2) of 'year + 1' since FY runs from Apr to Mar
        if (quarter === 1) {
          return m >= 3 && m <= 5 && y === year;
        } else if (quarter === 2) {
          return m >= 6 && m <= 8 && y === year;
        } else if (quarter === 3) {
          return m >= 9 && m <= 11 && y === year;
        } else if (quarter === 4) {
          return m >= 0 && m <= 2 && y === year + 1;
        }
        return false;
      }
    });
  };

  const currentSales = filterByPeriod(salesInvoices);
  const currentPurchases = filterByPeriod(purchaseInvoices);

  const calculateGST = (invoices: Invoice[]) => {
    return invoices.reduce((acc, curr) => {
      return acc + (Number(curr.SGSTAmount) || 0) + (Number(curr.CGSTAmount) || 0) + (Number(curr.IGSTAmount) || 0);
    }, 0);
  };
  
  const calculateTotal = (invoices: Invoice[]) => {
    return invoices.reduce((acc, curr) => acc + (Number(curr.Total_Amount) || 0), 0);
  };

  const gstCollected = calculateGST(currentSales);
  const gstPaid = calculateGST(currentPurchases);
  const netGstPayable = gstCollected - gstPaid;

  const totalSalesAmount = calculateTotal(currentSales);
  const totalPurchaseAmount = calculateTotal(currentPurchases);

  const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  const years = Array.from(new Array(10), (val, index) => new Date().getFullYear() - index);
  const quarters = [
    { id: 1, label: "Q1 (Apr - Jun)" },
    { id: 2, label: "Q2 (Jul - Sep)" },
    { id: 3, label: "Q3 (Oct - Dec)" },
    { id: 4, label: "Q4 (Jan - Mar)" }
  ];

  return (
    <div className="max-w-7xl mx-auto py-10 px-4">
      <h2 className="text-3xl font-extrabold mb-6 text-blue-800 tracking-tight text-center">GST Report</h2>
      
      <div className="bg-white shadow-lg rounded-2xl border p-6 mb-8 flex flex-wrap gap-6 justify-center items-end">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">Company</label>
          <select className="form-select border-2 border-gray-200 rounded-lg p-2 min-w-[200px]" value={companyId} onChange={e => setCompanyId(e.target.value)}>
            <option value="">-- Select Company --</option>
            {companies.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">Period Type</label>
          <select className="form-select border-2 border-gray-200 rounded-lg p-2" value={reportType} onChange={e => setReportType(e.target.value as any)}>
            <option value="monthly">Monthly</option>
            <option value="quarterly">Quarterly (FY)</option>
          </select>
        </div>
        {reportType === 'monthly' ? (
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Month</label>
            <select className="form-select border-2 border-gray-200 rounded-lg p-2" value={month} onChange={e => setMonth(Number(e.target.value))}>
              {months.map((m, i) => <option key={i} value={i}>{m}</option>)}
            </select>
          </div>
        ) : (
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Quarter</label>
            <select className="form-select border-2 border-gray-200 rounded-lg p-2" value={quarter} onChange={e => setQuarter(Number(e.target.value))}>
              {quarters.map(q => <option key={q.id} value={q.id}>{q.label}</option>)}
            </select>
          </div>
        )}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">{reportType === 'monthly' ? 'Year' : 'Financial Year (Starting)'}</label>
          <select className="form-select border-2 border-gray-200 rounded-lg p-2" value={year} onChange={e => setYear(Number(e.target.value))}>
            {years.map(y => (
              <option key={y} value={y}>
                {reportType === 'monthly' ? y : `${y}-${String(y + 1).slice(-2)}`}
              </option>
            ))}
          </select>
        </div>
        {companyId && (currentSales.length > 0 || currentPurchases.length > 0) && (
          <div className="flex-shrink-0">
            <button
              onClick={() => {
                const comp = companies.find(c => String(c.id) === String(companyId));
                const gstPayload = {
                  gstin: comp?.company_name || 'GSTIN_NOT_SET',
                  fp: reportType === 'monthly' ? `${String(month + 1).padStart(2, '0')}${year}` : `Q${quarter}${year}`,
                  version: "GST1.0",
                  hash: "hash_value",
                  b2b: currentSales.map(inv => {
                    const sgst = Number(inv.SGSTAmount) || 0;
                    const cgst = Number(inv.CGSTAmount) || 0;
                    const igst = Number(inv.IGSTAmount) || 0;
                    const taxVal = sgst + cgst + igst;
                    const baseVal = Number(inv.Total_Amount) - taxVal;
                    return {
                      ctin: "CUSTOMER_GSTIN", // Placeholder for GSTR-1 submission
                      inv: [
                        {
                          inum: inv.InvoiceNo,
                          idt: inv.Date ? new Date(inv.Date).toLocaleDateString('en-GB') : '',
                          val: Number(inv.Total_Amount),
                          pos: "07",
                          rchrg: "N",
                          inv_typ: "R",
                          itms: [
                            {
                              num: 1,
                              itm_det: {
                                txval: baseVal,
                                rt: taxVal > 0 ? Math.round((taxVal / baseVal) * 100) : 18,
                                iamt: igst,
                                camt: cgst,
                                samt: sgst
                              }
                            }
                          ]
                        }
                      ]
                    };
                  }),
                  b2bur: currentPurchases.map(pur => {
                    const sgst = Number(pur.SGSTAmount) || 0;
                    const cgst = Number(pur.CGSTAmount) || 0;
                    const igst = Number(pur.IGSTAmount) || 0;
                    const taxVal = sgst + cgst + igst;
                    const baseVal = Number(pur.Total_Amount) - taxVal;
                    return {
                      inv: [
                        {
                          inum: pur.InvoiceNo,
                          idt: pur.Date ? new Date(pur.Date).toLocaleDateString('en-GB') : '',
                          val: Number(pur.Total_Amount),
                          itms: [
                            {
                              num: 1,
                              itm_det: {
                                txval: baseVal,
                                rt: taxVal > 0 ? Math.round((taxVal / baseVal) * 100) : 18,
                                iamt: igst,
                                camt: cgst,
                                samt: sgst
                              }
                            }
                          ]
                        }
                      ]
                    };
                  })
                };
                const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(gstPayload, null, 2));
                const downloadAnchor = document.createElement('a');
                downloadAnchor.setAttribute("href", dataStr);
                downloadAnchor.setAttribute("download", `GST_FILING_${reportType === 'monthly' ? `MONTH_${month + 1}` : `Q${quarter}`}_${year}.json`);
                document.body.appendChild(downloadAnchor);
                downloadAnchor.click();
                downloadAnchor.remove();
              }}
              className="px-4 py-2 bg-gradient-to-r from-blue-700 to-indigo-700 hover:from-blue-800 hover:to-indigo-800 text-white rounded-lg text-xs font-bold shadow uppercase transition-all"
            >
              📥 Export GST Portal JSON
            </button>
          </div>
        )}
      </div>

      {!companyId ? (
        <div className="text-center text-gray-500 py-10 bg-white rounded-2xl shadow border">Please select a company to view the GST Report.</div>
      ) : loading ? (
        <div className="text-center text-gray-500 py-10 bg-white rounded-2xl shadow border">Loading data...</div>
      ) : (
        <div className="space-y-8">
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-blue-50 border border-blue-200 rounded-2xl p-6 text-center shadow">
              <h3 className="text-blue-800 font-bold text-lg mb-2">GST Collected (Sales)</h3>
              <p className="text-3xl font-extrabold text-blue-900">₹ {gstCollected.toLocaleString("en-IN", {minimumFractionDigits: 2})}</p>
              <p className="text-sm text-gray-500 mt-2">from {currentSales.length} invoices (Total: ₹ {totalSalesAmount.toLocaleString("en-IN")})</p>
            </div>
            <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-6 text-center shadow">
              <h3 className="text-yellow-800 font-bold text-lg mb-2">GST Paid (Purchases)</h3>
              <p className="text-3xl font-extrabold text-yellow-900">₹ {gstPaid.toLocaleString("en-IN", {minimumFractionDigits: 2})}</p>
              <p className="text-sm text-gray-500 mt-2">from {currentPurchases.length} invoices (Total: ₹ {totalPurchaseAmount.toLocaleString("en-IN")})</p>
            </div>
            <div className={`border rounded-2xl p-6 text-center shadow ${netGstPayable > 0 ? "bg-red-50 border-red-200" : "bg-green-50 border-green-200"}`}>
              <h3 className={`font-bold text-lg mb-2 ${netGstPayable > 0 ? "text-red-800" : "text-green-800"}`}>Net GST Payable</h3>
              <p className={`text-3xl font-extrabold ${netGstPayable > 0 ? "text-red-900" : "text-green-900"}`}>
                ₹ {Math.abs(netGstPayable).toLocaleString("en-IN", {minimumFractionDigits: 2})}
              </p>
              <p className={`text-sm mt-2 font-medium ${netGstPayable > 0 ? "text-red-600" : "text-green-600"}`}>
                {netGstPayable > 0 ? "To be paid to Govt." : "Available as Input Tax Credit (ITC)"}
              </p>
            </div>
          </div>

          {/* Detailed Sales Invoice Breakdown */}
          <div className="bg-white rounded-2xl shadow border overflow-hidden">
            <div className="bg-blue-100 px-6 py-3 border-b">
              <h3 className="text-blue-900 font-bold text-lg">Sales Invoices - GST Breakdown</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-blue-50 text-blue-900">
                    <th className="py-2 px-3 text-left font-semibold">#</th>
                    <th className="py-2 px-3 text-left font-semibold">Invoice No.</th>
                    <th className="py-2 px-3 text-left font-semibold">Date</th>
                    <th className="py-2 px-3 text-right font-semibold">Amount (₹)</th>
                    <th className="py-2 px-3 text-right font-semibold">SGST (₹)</th>
                    <th className="py-2 px-3 text-right font-semibold">CGST (₹)</th>
                    <th className="py-2 px-3 text-right font-semibold">IGST (₹)</th>
                    <th className="py-2 px-3 text-right font-semibold">Total GST (₹)</th>
                  </tr>
                </thead>
                <tbody>
                  {currentSales.length > 0 ? currentSales.map((inv: any, idx: number) => {
                    const invGst = (Number(inv.SGSTAmount) || 0) + (Number(inv.CGSTAmount) || 0) + (Number(inv.IGSTAmount) || 0);
                    return (
                      <tr key={inv.id || idx} className={idx % 2 ? "bg-gray-50" : ""}>
                        <td className="py-2 px-3">{idx + 1}</td>
                        <td className="py-2 px-3 font-medium text-blue-700">
                          {inv.id ? (
                            <a href={`/editinvoice?id=${inv.id}`} className="underline hover:text-blue-900 cursor-pointer transition-colors">{inv.InvoiceNo || '-'}</a>
                          ) : (inv.InvoiceNo || '-')}
                        </td>
                        <td className="py-2 px-3">{inv.Date ? new Date(inv.Date).toLocaleDateString("en-IN") : '-'}</td>
                        <td className="py-2 px-3 text-right">{Number(inv.Total_Amount || 0).toLocaleString("en-IN")}</td>
                        <td className="py-2 px-3 text-right">{Number(inv.SGSTAmount || 0).toLocaleString("en-IN")}</td>
                        <td className="py-2 px-3 text-right">{Number(inv.CGSTAmount || 0).toLocaleString("en-IN")}</td>
                        <td className="py-2 px-3 text-right">{Number(inv.IGSTAmount || 0).toLocaleString("en-IN")}</td>
                        <td className="py-2 px-3 text-right font-bold text-blue-800">{invGst.toLocaleString("en-IN")}</td>
                      </tr>
                    );
                  }) : (
                    <tr><td colSpan={8} className="py-6 text-center text-gray-400">No sales invoices for this period.</td></tr>
                  )}
                  {currentSales.length > 0 && (
                    <tr className="bg-blue-100 font-bold text-blue-900">
                      <td colSpan={3} className="py-2 px-3">TOTAL</td>
                      <td className="py-2 px-3 text-right">{totalSalesAmount.toLocaleString("en-IN")}</td>
                      <td className="py-2 px-3 text-right">{currentSales.reduce((s: number, i: any) => s + (Number(i.SGSTAmount) || 0), 0).toLocaleString("en-IN")}</td>
                      <td className="py-2 px-3 text-right">{currentSales.reduce((s: number, i: any) => s + (Number(i.CGSTAmount) || 0), 0).toLocaleString("en-IN")}</td>
                      <td className="py-2 px-3 text-right">{currentSales.reduce((s: number, i: any) => s + (Number(i.IGSTAmount) || 0), 0).toLocaleString("en-IN")}</td>
                      <td className="py-2 px-3 text-right">{gstCollected.toLocaleString("en-IN")}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Detailed Purchase Invoice Breakdown */}
          <div className="bg-white rounded-2xl shadow border overflow-hidden">
            <div className="bg-yellow-100 px-6 py-3 border-b">
              <h3 className="text-yellow-900 font-bold text-lg">Purchase Invoices - GST Breakdown (ITC)</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-yellow-50 text-yellow-900">
                    <th className="py-2 px-3 text-left font-semibold">#</th>
                    <th className="py-2 px-3 text-left font-semibold">Invoice No.</th>
                    <th className="py-2 px-3 text-left font-semibold">Date</th>
                    <th className="py-2 px-3 text-right font-semibold">Amount (₹)</th>
                    <th className="py-2 px-3 text-right font-semibold">SGST (₹)</th>
                    <th className="py-2 px-3 text-right font-semibold">CGST (₹)</th>
                    <th className="py-2 px-3 text-right font-semibold">IGST (₹)</th>
                    <th className="py-2 px-3 text-right font-semibold">Total GST / ITC (₹)</th>
                  </tr>
                </thead>
                <tbody>
                  {currentPurchases.length > 0 ? currentPurchases.map((inv: any, idx: number) => {
                    const invGst = (Number(inv.SGSTAmount) || 0) + (Number(inv.CGSTAmount) || 0) + (Number(inv.IGSTAmount) || 0);
                    return (
                      <tr key={inv.id || idx} className={idx % 2 ? "bg-gray-50" : ""}>
                        <td className="py-2 px-3">{idx + 1}</td>
                        <td className="py-2 px-3 font-medium text-yellow-700">
                          {inv.id ? (
                            <a href={`/purchase-invoices`} className="underline hover:text-yellow-900 cursor-pointer transition-colors">{inv.InvoiceNo || '-'}</a>
                          ) : (inv.InvoiceNo || '-')}
                        </td>
                        <td className="py-2 px-3">{inv.Date ? new Date(inv.Date).toLocaleDateString("en-IN") : '-'}</td>
                        <td className="py-2 px-3 text-right">{Number(inv.Total_Amount || 0).toLocaleString("en-IN")}</td>
                        <td className="py-2 px-3 text-right">{Number(inv.SGSTAmount || 0).toLocaleString("en-IN")}</td>
                        <td className="py-2 px-3 text-right">{Number(inv.CGSTAmount || 0).toLocaleString("en-IN")}</td>
                        <td className="py-2 px-3 text-right">{Number(inv.IGSTAmount || 0).toLocaleString("en-IN")}</td>
                        <td className="py-2 px-3 text-right font-bold text-yellow-800">{invGst.toLocaleString("en-IN")}</td>
                      </tr>
                    );
                  }) : (
                    <tr><td colSpan={8} className="py-6 text-center text-gray-400">No purchase invoices for this period.</td></tr>
                  )}
                  {currentPurchases.length > 0 && (
                    <tr className="bg-yellow-100 font-bold text-yellow-900">
                      <td colSpan={3} className="py-2 px-3">TOTAL</td>
                      <td className="py-2 px-3 text-right">{totalPurchaseAmount.toLocaleString("en-IN")}</td>
                      <td className="py-2 px-3 text-right">{currentPurchases.reduce((s: number, i: any) => s + (Number(i.SGSTAmount) || 0), 0).toLocaleString("en-IN")}</td>
                      <td className="py-2 px-3 text-right">{currentPurchases.reduce((s: number, i: any) => s + (Number(i.CGSTAmount) || 0), 0).toLocaleString("en-IN")}</td>
                      <td className="py-2 px-3 text-right">{currentPurchases.reduce((s: number, i: any) => s + (Number(i.IGSTAmount) || 0), 0).toLocaleString("en-IN")}</td>
                      <td className="py-2 px-3 text-right">{gstPaid.toLocaleString("en-IN")}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GstReport;
